import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,

  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { apiService, ProcessRequest } from '../services/apiService';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const AnalysisPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: pendingImages, isLoading: pendingLoading, refetch: refetchPending } = useQuery({
    queryKey: ['pendingAnalysis'],
    queryFn: () => apiService.getPendingAnalysis(),
  });

  const analysisMutation = useMutation({
    mutationFn: (data: ProcessRequest) => apiService.startProcess(data),
    onSuccess: (result) => {
      setAnalysisResults(result);
      queryClient.invalidateQueries({ queryKey: ['pendingAnalysis'] });
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setAnalysisResults(null);
  };

  const handleAnalyzeImage = (imageId: string, bodyPart?: string) => {
    if (!user) return;
    
    analysisMutation.mutate({
      username: user.username,
      image_id: imageId,
      body_part: bodyPart,
    });
  };

  const handleManualAnalysis = (bodyPart?: string) => {
    if (!user) return;
    
    analysisMutation.mutate({
      username: user.username,
      body_part: bodyPart,
    });
  };

  const renderAnalysisDetails = (analysis: any) => {
    if (!analysis) return null;

    const getClassificationColor = (classification: string) => {
      switch (classification.toLowerCase()) {
        case 'benign': return 'success';
        case 'malignant': return 'error';
        case 'other': return 'warning';
        default: return 'default';
      }
    };

    const confidencePercentage = (analysis.confidence_level * 100).toFixed(1);

    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          📊 Analysis Results
        </Typography>

        {/* ABCDE Criteria */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">ABCDE Criteria</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">A - Asymmetry:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.asymmetry}</Typography>
                
                <Typography variant="subtitle2">B - Border Irregularity:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.border_irregularity}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">C - Color Variegation:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.color_variegation}</Typography>
                
                <Typography variant="subtitle2">D - Diameter Assessment:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.diameter_assessment}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">E - Evolution Assessment:</Typography>
                <Typography variant="body2">{analysis.evolution_assessment}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Technical Analysis */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Technical Analysis</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Segmentation Analysis:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.segmentation_analysis}</Typography>
                
                <Typography variant="subtitle2">Technical Comments:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.image_technical_commentaries}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Other Possible Diagnoses:</Typography>
                <Typography variant="body2" gutterBottom>{analysis.other_diagnoses}</Typography>
                
                <Typography variant="subtitle2">Additional Info Influence:</Typography>
                <Typography variant="body2">{analysis.extra_info_influence}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Final Assessment */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            🔬 Preliminary Preventive Assessment
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            ⚠️ This is a preliminary AI-assisted evaluation that will help doctors and does not constitute a conclusive medical diagnosis.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preliminary preventive assessment:
                </Typography>
                <Chip
                  label={analysis.advance_classification}
                  color={getClassificationColor(analysis.advance_classification)}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Model Confidence Level: {confidencePercentage}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={analysis.confidence_level * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                General Observations:
              </Typography>
              <Typography variant="body2">
                {analysis.final_explanation}
              </Typography>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            👨‍⚕️ Remember: The best prevention is regular review with a healthcare professional.
          </Alert>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        🔍 Advanced Analysis
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>What is the ABCDE rule?</strong><br />
          <strong>A - Asymmetry:</strong> Lesions that are not uniform in shape.<br />
          <strong>B - Border:</strong> Irregular, jagged, or poorly defined borders.<br />
          <strong>C - Color:</strong> Various shades, including brown, black, red, blue, or white.<br />
          <strong>D - Diameter:</strong> Greater than 6 mm.<br />
          <strong>E - Evolution:</strong> Changes over time (size, shape, color, elevation, or symptoms).
        </Typography>
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="📥 Pending Images" />
          <Tab label="🔍 Manual Analysis" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Images Pending Advanced Analysis</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetchPending()}
            disabled={pendingLoading}
          >
            Refresh List
          </Button>
        </Box>

        {pendingLoading ? (
          <LoadingSpinner message="Loading pending images..." />
        ) : !pendingImages || pendingImages.length === 0 ? (
          <Alert severity="info">
            No images pending advanced analysis.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {pendingImages.map((image) => (
              <Grid item xs={12} md={6} key={image.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Image from {format(new Date(image.timestamp), 'dd MMM yyyy, HH:mm')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>ID:</strong> {image.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Body Part:</strong> {image.body_part || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Initial Classification:</strong> {image.first_classification || 'Not available'}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      startIcon={<ScienceIcon />}
                      onClick={() => handleAnalyzeImage(image.id, image.body_part)}
                      disabled={analysisMutation.isPending}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      {analysisMutation.isPending ? 'Analyzing...' : '🔍 Analyze'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {analysisMutation.isPending && (
          <Box sx={{ mt: 3 }}>
            <LoadingSpinner message="Performing advanced analysis..." />
          </Box>
        )}

        {analysisMutation.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Analysis failed: {(analysisMutation.error as any)?.response?.data?.detail || 'Unknown error'}
          </Alert>
        )}

        {analysisResults && renderAnalysisDetails(analysisResults.analysis)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>
          Manual Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          This option will generally start analysis on the most recent image uploaded by the specified user 
          (optionally filtered by body part).
        </Typography>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Analyze Most Recent Image
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Will analyze the most recent image for user: {user?.username}
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<ScienceIcon />}
              onClick={() => handleManualAnalysis()}
              disabled={analysisMutation.isPending}
              sx={{ mt: 2 }}
            >
              {analysisMutation.isPending ? 'Analyzing...' : '🛠️ Start Manual Analysis'}
            </Button>
          </CardContent>
        </Card>

        {analysisMutation.isPending && (
          <Box sx={{ mt: 3 }}>
            <LoadingSpinner message="Starting manual analysis..." />
          </Box>
        )}

        {analysisMutation.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Analysis failed: {(analysisMutation.error as any)?.response?.data?.detail || 'Unknown error'}
          </Alert>
        )}

        {analysisResults && renderAnalysisDetails(analysisResults.analysis)}
      </TabPanel>

      <Box sx={{ mt: 4 }}>
        <Divider />
        <Alert severity="info" sx={{ mt: 2 }}>
          If you want to see all your images and results, check the History page.
        </Alert>
      </Box>
    </Box>
  );
};

export default AnalysisPage;
